/**
 * SiteOwnerDashboard — Organisation Dashboard
 *
 * Panels by role:
 *   admin      — system-wide totals + all-org breakdown + subscription + personal stats
 *   site_owner — their org totals + per-org breakdown + subscription + personal stats
 *   contributor— personal submission stats only
 *
 * Data source: GET /api/dashboard/summary  (DashboardController)
 */
import { Link } from 'react-router-dom';
import { formatCount } from '@/lib/utils';
import { useDashboardSummary } from '@/hooks/useDashboard';
import { useLicenses } from '@/hooks/useBilling';
import { useAuth } from '@/components/auth/AuthContext';

// ── Shared primitives ─────────────────────────────────────────────────────────

const COLOR_MAP = {
  brand:  'bg-red-50  border-red-200  text-[#003cc5]',
  green:  'bg-emerald-50 border-emerald-200 text-emerald-700',
  purple: 'bg-violet-50  border-violet-200  text-violet-700',
  amber:  'bg-amber-50  border-amber-200  text-amber-700',
  slate:  'bg-paper border-neutral-200 text-neutral-600',
  blue:   'bg-blue-50  border-blue-200   text-blue-700',
};

function MetricCard({ label, value, sub, color = 'slate', badge }) {
  return (
    <div className={`rounded-2xl border p-5 ${COLOR_MAP[color] ?? COLOR_MAP.slate}`}>
      <div className="mb-1 flex items-center justify-between">
        <p className="text-xs font-medium uppercase tracking-wider opacity-70">{label}</p>
        {badge && (
          <span className="rounded-full bg-white/60 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider opacity-80">
            {badge}
          </span>
        )}
      </div>
      <p className="text-3xl font-bold">{value}</p>
      {sub && <p className="mt-1 text-xs opacity-60">{sub}</p>}
    </div>
  );
}

function SectionHeading({ children, admin }) {
  return (
    <div className="mb-4 flex items-center gap-2">
      <h2 className="text-base font-semibold text-ink">{children}</h2>
      {admin && (
        <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-violet-700">
          Admin
        </span>
      )}
    </div>
  );
}

function Skeleton({ className = 'h-28' }) {
  return <div className={`animate-skeleton rounded-2xl bg-paper ${className}`} />;
}

// ── Subscription status badge ─────────────────────────────────────────────────

function SubscriptionBadge({ licenses, isLoading }) {
  // Pick the best license to display: prefer active, then pending, then first.
  const license = licenses?.find((l) => l.state === 'active')
    ?? licenses?.find((l) => l.state === 'pending')
    ?? licenses?.[0]
    ?? null;

  const state = license?.state ?? 'none';

  const styles =
    state === 'active'    ? 'bg-emerald-50 border-emerald-200 text-emerald-800' :
    state === 'pending'   ? 'bg-amber-50   border-amber-200   text-amber-800'   :
    state === 'cancelled' ? 'bg-red-50     border-red-200     text-[#003cc5]'   :
    state === 'expired'   ? 'bg-red-50     border-red-200     text-[#003cc5]'   :
                            'bg-neutral-50 border-neutral-200 text-neutral-500' ;

  const dot =
    state === 'active'    ? 'bg-emerald-500' :
    state === 'pending'   ? 'bg-amber-500'   :
    state === 'cancelled' ? 'bg-[#003cc5]'   :
    state === 'expired'   ? 'bg-[#003cc5]'   :
                            'bg-neutral-400'  ;

  const label =
    state === 'active'    ? 'Active'    :
    state === 'pending'   ? 'Pending'   :
    state === 'cancelled' ? 'Cancelled' :
    state === 'expired'   ? 'Expired'   :
                            'No Plan'   ;

  const planLine = license
    ? [license.tier, license.period].filter(Boolean).join(' · ')
    : null;

  const expiryLine = license?.expires
    ? `expires ${new Date(license.expires).toLocaleDateString()}`
    : license ? 'unlimited' : null;

  const sub = [planLine, expiryLine].filter(Boolean).join(' — ') || null;

  if (isLoading) {
    return <div className="h-14 animate-skeleton rounded-2xl bg-paper" />;
  }

  return (
    <div className={`rounded-2xl border p-4 flex items-center gap-3 ${styles}`}>
      <span className={`h-2.5 w-2.5 flex-none rounded-full ${dot}`} />
      <div className="min-w-0">
        <p className="text-sm font-semibold capitalize">{label}</p>
        {sub && <p className="truncate text-xs opacity-70 capitalize">{sub}</p>}
      </div>
    </div>
  );
}

// ── Org breakdown table ───────────────────────────────────────────────────────

function OrgTable({ orgs }) {
  if (!orgs?.length) {
    return (
      <div className="rounded-2xl border border-neutral-200 bg-paper">
        <p className="p-8 text-center text-sm text-neutral-500">No organisations found.</p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-neutral-200 bg-paper overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-neutral-200 text-left text-xs uppercase tracking-wider text-neutral-500">
            <th className="px-5 py-3">Organisation</th>
            <th className="px-5 py-3 text-right">Members</th>
            <th className="px-5 py-3 text-right">Events</th>
            <th className="px-5 py-3 text-right">Lives</th>
            <th className="px-5 py-3">Top Event Type</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-200">
          {orgs.map((org) => (
            <tr key={org.id} className="hover:bg-parchment">
              <td className="max-w-[180px] truncate px-5 py-3 font-medium text-ink">{org.label}</td>
              <td className="px-5 py-3 text-right text-neutral-500">{formatCount(org.member_count)}</td>
              <td className="px-5 py-3 text-right text-neutral-500">{formatCount(org.catalyst_count)}</td>
              <td className="px-5 py-3 text-right text-neutral-500">{formatCount(org.lives_affected)}</td>
              <td className="px-5 py-3 text-neutral-500 max-w-[160px] truncate">
                {org.top_method ?? org.top_event_type ?? '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Personal stats panel ──────────────────────────────────────────────────────

function PersonalStatsPanel({ stats }) {
  if (!stats) return null;

  return (
    <div className="rounded-2xl border border-neutral-200 bg-paper overflow-hidden">
      <div className="border-b border-neutral-200 px-5 py-3.5">
        <h3 className="text-sm font-semibold text-ink">My Submissions</h3>
      </div>
      <div className="grid grid-cols-3 divide-x divide-neutral-200">
        {[
          { label: 'Events',        value: formatCount(stats.catalyst_count) },
          { label: 'Lives Affected', value: formatCount(stats.lives_affected) },
          { label: 'Top Event Type',    value: stats.top_method ?? stats.top_event_type ?? '—' },
        ].map(({ label, value }) => (
          <div key={label} className="p-5 text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-neutral-400">{label}</p>
            <p className="mt-1 truncate text-lg font-bold text-ink" title={value}>{value}</p>
          </div>
        ))}
      </div>
      <div className="border-t border-neutral-200 px-5 py-3">
        <Link
          to="/my-reports"
          className="text-xs font-medium text-[#003cc5] hover:underline"
        >
          View my reports →
        </Link>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function SiteOwnerDashboard() {
  const { user } = useAuth();
  const { data, isLoading, isError } = useDashboardSummary();
  const { data: licenses, isLoading: licensesLoading } = useLicenses();

  const role        = data?.role ?? 'contributor';
  const isAdmin     = role === 'admin';
  const isSiteOwner = role === 'site_owner' || isAdmin;
  const totals      = data?.totals;
  const orgs        = data?.orgs ?? [];
  const myStats     = data?.my_stats;

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-ink">Dashboard</h1>
            {isAdmin && (
              <span className="rounded-full bg-violet-100 px-2.5 py-0.5 text-xs font-semibold text-violet-700">
                Admin
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-neutral-500">
            {isAdmin     ? 'System-wide impact metrics and organisation overview' :
             isSiteOwner ? 'Organisation impact metrics and billing overview'     :
                           'Your personal submission statistics'}
          </p>
        </div>
        <Link
          to="/timelines"
          className="flex items-center gap-2 rounded-xl border border-neutral-200 px-4 py-2 text-sm text-neutral-600 transition hover:border-[#003cc5] hover:text-ink"
        >
          📊 Timelines
        </Link>
      </div>

      {isError && (
        <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-700">
          Failed to load dashboard data. Check Drupal logs for details.
        </div>
      )}

      {/* ── Org-level summary cards (admin + site_owner) ─────────────────── */}
      {isSiteOwner && (
        <>
          <div className="mb-2">
            <SectionHeading admin={isAdmin}>
              {isAdmin ? 'System-Wide Totals' : 'Organisation Totals'}
            </SectionHeading>
          </div>

          <div className="mb-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
            {isLoading ? (
              [1, 2, 3, 4].map((i) => <Skeleton key={i} />)
            ) : (
              <>
                <MetricCard
                  label="Catalyst Events"
                  value={formatCount(totals?.catalyst_count ?? 0)}
                  sub={`across ${totals?.orgs_count ?? 0} org${totals?.orgs_count !== 1 ? 's' : ''}`}
                  color="brand"
                />
                <MetricCard
                  label="Lives Affected"
                  value={formatCount(totals?.lives_affected ?? 0)}
                  sub="total reported impact"
                  color="green"
                />
                <MetricCard
                  label="People in Orgs"
                  value={formatCount(totals?.member_count ?? 0)}
                  sub="active members"
                  color="purple"
                />
                <MetricCard
                  label="Most Impactful Event Type"
                  value={totals?.top_method ?? '—'}
                  sub="by lives affected"
                  color="amber"
                />
              </>
            )}
          </div>

          {/* ── Org breakdown + billing ──────────────────────────────────────── */}
          <div className="mb-6">
            <SectionHeading admin={isAdmin}>Organisation Breakdown</SectionHeading>
            {isLoading ? (
              <Skeleton className="h-48" />
            ) : (
              <OrgTable orgs={orgs} />
            )}
          </div>

          {/* ── Subscription status ──────────────────────────────────────────── */}
          <div className="mb-8">
            <SubscriptionBadge licenses={licenses} isLoading={licensesLoading} />
          </div>
        </>
      )}

      {/* ── Contributor-only message ─────────────────────────────────────────── */}
      {!isSiteOwner && !isLoading && (
        <div className="mb-8 rounded-2xl border border-neutral-200 bg-paper p-6 text-center">
          <p className="text-sm font-medium text-ink">You are logged in as a Contributor.</p>
          <p className="mt-1 text-xs text-neutral-500">
            Organisation-level stats are visible to Site Owners and Admins.
          </p>
        </div>
      )}

      {/* ── Personal stats (all roles) ───────────────────────────────────────── */}
      <div>
        <SectionHeading>My Activity</SectionHeading>
        {isLoading ? (
          <Skeleton className="h-28" />
        ) : (
          <PersonalStatsPanel stats={myStats} />
        )}
      </div>
    </div>
  );
}
